import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import NewsletterTable from '../../../components/tables/NewsletterTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useNewsletterApi } from '../../../hooks/useNewsletterApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { NewsletterDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useNewsletterCategories } from '../../../hooks/useNewsletterCategoryApi';

const NewsletterList: React.FC = () => {
  const { search, remove, isLoading, error } = useNewsletterApi();
  const [items, setItems] = useState<NewsletterDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Newsletters', href: '#' },
  ];

  const { data: newsletterCategoriesData } = useNewsletterCategories();

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Newsletters:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: NewsletterDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting Newsletter:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/newsletters"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Content.Newsletter.View"
    >
      <Card
        title="Manage Newsletters"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/newsletters/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
            <FormField
              id="year"
              label="Search by Year"
              type='number'
              value={searchFilters.year || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, year: e.target.value })
              }
              placeholder="Search by Year"
            />
            <FormField
              id="volume"
              label="Search by Volume"
              value={searchFilters.volume || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, volume: e.target.value })
              }
              placeholder="Search by Volume"
            />
            <FormField
              id="date"
              label="Search by Date"
              type='date'
              value={searchFilters.date || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, date: e.target.value })
              }
              placeholder="Search by Date"
            />
            <FormField
              id="newsletterCategoryId"
              label="Search by Category"
              type="dropdown"
              value={searchFilters.newsletterCategoryId || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  newsletterCategoryId: e.target.value,
                })
              }
              placeholder="All Categories"
              options={(newsletterCategoriesData || []).map((nc) => ({
                value: nc.id || '',
                label: nc.title || '',
              }))}
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <NewsletterTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/newsletters/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default NewsletterList;
